// ScegliPaese.tsx — v037: scelta del paese in una finestra con la X (Wall of Fame e profilo). Prima era un <select>:
// su Android apriva un elenco a tutto schermo da chiudere con il tasto indietro del telefono. Qui: un pulsante con la
// bandiera e il nome; tocca → finestra con «Cerca un paese» e l'elenco (bandiera, nome nella lingua dell'app).
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Finestra } from "./Finestra.tsx";
import { vibra } from "../servizi/nativo.ts";

/** La bandiera come emoji (le due «lettere regionali» del codice ISO), per esempio IT → 🇮🇹. */
export function bandieraPaese(codice: string): string {
  const c = codice.toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "🌍";
  return String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65, 0x1f1e6 + c.charCodeAt(1) - 65);
}

/** Toglie accenti e maiuscole, per cercare «Espana» e trovare «España». */
export function perCercare(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
}

export function ScegliPaese({
  paesi,
  valore,
  onScegli,
  etichetta,
  nessuno,
}: {
  /** codici ISO già in ordine con il nome nella lingua dell'app */
  paesi: readonly { c: string; n: string }[];
  valore: string | null;
  onScegli: (c: string | null) => void;
  etichetta: string;
  /** il testo della scelta vuota («Tutto il mondo», «Non indicato») */
  nessuno: string;
}) {
  const { t } = useTranslation();
  const [aperta, setAperta] = useState(false);
  const [cerca, setCerca] = useState("");
  const scelto = valore ? paesi.find((p) => p.c === valore) : null;
  const visibili = useMemo(() => {
    const q = perCercare(cerca);
    return q ? paesi.filter((p) => perCercare(p.n).includes(q) || p.c.toLowerCase() === q) : paesi;
  }, [paesi, cerca]);
  const scegli = (c: string | null) => {
    vibra("scelta");
    onScegli(c);
    setAperta(false);
    setCerca("");
  };
  return (
    <div className="campo-etichettato">
      <span className="etichetta">{etichetta}</span>
      <button type="button" className="campo ld-paese-pulsante" aria-haspopup="dialog" onClick={() => setAperta(true)}>
        <span className="ld-paese-bandiera" aria-hidden="true">
          {scelto ? bandieraPaese(scelto.c) : "🌍"}
        </span>
        <span className="ld-paese-nome">{scelto ? scelto.n : nessuno}</span>
        <svg className="ld-paese-freccia" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <Finestra aperta={aperta} titolo={etichetta} onChiudi={() => setAperta(false)}>
        <input
          className="campo ld-paese-cerca"
          type="search"
          value={cerca}
          placeholder={t("paese.cerca")}
          aria-label={t("paese.cerca")}
          onChange={(e) => setCerca(e.target.value)}
        />
        <div className="ld-paesi" role="radiogroup" aria-label={etichetta}>
          {!cerca && (
            <button type="button" role="radio" aria-checked={valore === null} className="ld-paese-voce" onClick={() => scegli(null)}>
              <span className="ld-paese-bandiera" aria-hidden="true">
                🌍
              </span>
              <span>{nessuno}</span>
            </button>
          )}
          {visibili.map((p) => (
            <button key={p.c} type="button" role="radio" aria-checked={valore === p.c} className="ld-paese-voce" onClick={() => scegli(p.c)}>
              <span className="ld-paese-bandiera" aria-hidden="true">
                {bandieraPaese(p.c)}
              </span>
              <span>{p.n}</span>
            </button>
          ))}
          {visibili.length === 0 && <p className="nota-centrata">{t("paese.nessuno_trovato")}</p>}
        </div>
      </Finestra>
    </div>
  );
}
